export const UNSYNCED_CLOCK_STATE = {
    synced: false,
    uncertaintyMs: null,
    clockSkewMs: null,
    lastSyncAt: null,
    lastSyncMonotonicMs: null,
};
/**
 * Current server time (epoch ms) from a {@link ServerClockState}, or `null` while unsynced.
 * The anchor is a `performance.now()` reading, so the state is only meaningful in the
 * context that produced it (a worker's state cannot be read on the main thread).
 */
export const serverNow = (state, monotonic = () => performance.now()) => state.lastSyncAt === null || state.lastSyncMonotonicMs === null
    ? null
    : Math.round(state.lastSyncAt + (monotonic() - state.lastSyncMonotonicMs));
export const createServerClock = ({ fetchServerTime, monotonic = () => performance.now(), }) => {
    let anchor = null;
    const subs = new Set();
    const read = (a) => Math.round(a.epoch + (monotonic() - a.mono));
    const now = () => (anchor ? read(anchor) : null);
    const sync = async (samples = 5) => {
        let best = null;
        let lastError;
        for (let i = 0; i < samples; i++) {
            const t0 = monotonic();
            try {
                const serverTime = await fetchServerTime();
                const t1 = monotonic();
                const uncertainty = (t1 - t0) / 2;
                if (!best || uncertainty < best.uncertainty) {
                    best = { epoch: serverTime + uncertainty, mono: t1, uncertainty };
                }
            }
            catch (error) {
                lastError = error;
            }
        }
        if (!best)
            throw lastError instanceof Error ? lastError : new Error("server clock sync failed", { cause: lastError });
        anchor = best;
        const next = state();
        for (const cb of subs)
            cb(next);
    };
    const state = () => anchor
        ? {
            synced: true,
            uncertaintyMs: anchor.uncertainty,
            clockSkewMs: read(anchor) - Date.now(),
            lastSyncAt: anchor.epoch,
            lastSyncMonotonicMs: anchor.mono,
        }
        : UNSYNCED_CLOCK_STATE;
    const subscribe = (cb) => {
        subs.add(cb);
        if (anchor)
            cb(state());
        return () => void subs.delete(cb);
    };
    return { now, sync, state, subscribe };
};
/**
 * A {@link ServerClock} whose time source arrives later: `now()`/`state()` report the
 * unsynced state until `configure` runs, and `sync()` calls issued earlier wait for
 * it. The shell owns one runtime; tests create their own.
 */
export const createServerClockRuntime = () => {
    let inner;
    let stopInner = () => { };
    const subs = new Set();
    let resolveReady = () => { };
    const ready = new Promise((resolve) => {
        resolveReady = resolve;
    });
    return {
        clock: {
            now: () => (inner ? inner.now() : null),
            // reads `inner` after the gate so a re-configure retargets later syncs to the current clock
            sync: async (samples) => {
                await ready;
                return inner.sync(samples);
            },
            state: () => (inner ? inner.state() : UNSYNCED_CLOCK_STATE),
            subscribe: (cb) => {
                subs.add(cb);
                const current = inner?.state();
                if (current?.synced)
                    cb(current);
                return () => void subs.delete(cb);
            },
        },
        configure: (deps) => {
            stopInner();
            inner = createServerClock(deps);
            stopInner = inner.subscribe((next) => {
                for (const cb of subs)
                    cb(next);
            });
            resolveReady();
        },
    };
};
let singleton;
const singletonRuntime = () => {
    singleton ??= createServerClockRuntime();
    return singleton;
};
/** Inject the time source once at app boot. `sync()` calls issued earlier wait and run once this does. */
export const configureServerClock = (deps) => {
    singletonRuntime().configure(deps);
};
/** The context-wide {@link ServerClock}. Consumers see its state through `artis.v1.time.subscribe`. */
export const serverClock = {
    now: () => singletonRuntime().clock.now(),
    sync: (samples) => singletonRuntime().clock.sync(samples),
    state: () => singletonRuntime().clock.state(),
    subscribe: (cb) => singletonRuntime().clock.subscribe(cb),
};
//# sourceMappingURL=serverClock.js.map